import { Icon, IconButton, HStack, VStack } from '@genial/components';
import { BoxRadius, Color, Spacing, Typography } from '@genial/design-tokens';
import { IconographyTypes } from '@genial/icons';
import { Utils, useBreakpointValue } from '@genial/utils';
import { useNavigation } from '@react-navigation/native';
import { Text, Pressable, ScrollView, Center, Actionsheet, useDisclose } from 'native-base';
import React, { useState } from 'react';

import { TableContentItemButton } from '../../../../../../components/TableContent';
import { SignedInNavigationScreenNavigationProp } from '../../../../../../routes/types';

import { options } from './data';

interface SimulateProps {}

type OptionItem = {
  id: number;
  name: string;
};

export const Simulate: React.FC<SimulateProps> = () => {
  const navigation = useNavigation<SignedInNavigationScreenNavigationProp>();
  const isMobile = useBreakpointValue({ base: true, sm: false });
  const [selectedCategory, setSelectedCategory] = useState(options[0]);

  const { isOpen, onOpen, onClose } = useDisclose();

  const handleSelect = (item: OptionItem) => {
    isMobile && onClose();
    const data = { selectedItem: item, selectedCategory };
    navigation.navigate('ConsortiumSimulateValue', data);
  };

  const items = options.find((opt) => opt.id === selectedCategory.id)?.items ?? [];

  const renderOptions = () => {
    if (items.length === 0) {
      return (
        <Center>
          <Text
            variant="semibold"
            fontSize={Typography['fontsize.lg']}
            color={Utils.ColorToken.WithOpacity('brand.primary.principal', 0.6)}>
            Nenhuma opção disponível.
          </Text>
        </Center>
      );
    }

    const renderedItems = items.map((option) => (
      <HStack
        key={option.id.toString()}
        alignItems="center"
        justifyContent="space-between"
        px={Spacing['inline.xxs']}
        py={Spacing['inline.xxxs']}
        borderRadius={BoxRadius.sm}
        bg={Utils.ColorToken.WithOpacity('brand.tertiary.lightest', 0.3)}>
        <Text
          variant="semibold"
          fontSize={Typography['fontsize.lg']}
          color={Utils.ColorToken.WithOpacity('brand.primary.principal', 0.6)}>
          {option.name}
        </Text>
        <TableContentItemButton forceDesktop onPress={() => handleSelect(option)} />
      </HStack>
    ));

    return renderedItems;
  };

  const renderCategories = () => {
    const renderedCategories = options.map((category) => {
      const isSelected = selectedCategory.id === category.id;

      return (
        <Pressable
          key={category.id.toString()}
          px={{ base: Spacing['inline.xxxs'], sm: Spacing['inline.xxs'] }}
          py={Spacing['inline.xxxs']}
          borderRadius={BoxRadius.sm}
          onPress={() => {
            setSelectedCategory(category);
            isMobile && onOpen();
          }}
          {...(isSelected &&
            !isMobile && { bg: Utils.ColorToken.WithOpacity('brand.tertiary.lightest', 0.3) })}
          {...((!isSelected || isMobile) && { borderWidth: 1, borderColor: 'neutral.lightest' })}>
          <VStack space={Spacing['inline.nano']}>
            <Icon
              name={category.icon as IconographyTypes.Option}
              size="sm"
              color={Color['brand.tertiary.principal']}
            />
            <Text
              color={Color['neutral.darkest']}
              fontWeight={300}
              fontSize={{ base: Typography['fontsize.md'], sm: Typography['fontsize.xl'] }}>
              {category.category}
            </Text>
          </VStack>
        </Pressable>
      );
    });

    return renderedCategories;
  };

  return (
    <VStack
      w="full"
      space={Spacing['inline.xxs']}
      {...(isMobile && { p: Spacing['inline.xxxs'], bg: 'white', borderRadius: BoxRadius.sm })}>
      <VStack space={Spacing['inline.quarck']}>
        <Text variant="bold" fontSize={Typography['fontsize.lg']}>
          O que deseja simular?
        </Text>
        <Text color="neutral.dark" variant="regular">
          Selecione uma das opções
        </Text>
      </VStack>
      <HStack space={Spacing['inline.xxs']}>
        <ScrollView
          w="full"
          maxW={{ base: 'full', sm: '425px' }}
          h="520px"
          showsVerticalScrollIndicator={false}>
          <VStack space={Spacing['inline.xxs']}>{renderCategories()}</VStack>
        </ScrollView>
        {!isMobile && (
          <ScrollView h="520px" w={{ sm: 'full' }} showsVerticalScrollIndicator={false}>
            <VStack space={Spacing['inline.xxxs']}>{renderOptions()}</VStack>
          </ScrollView>
        )}
      </HStack>
      <Actionsheet useRNModal isOpen={isOpen} onClose={onClose} size="full">
        <Actionsheet.Content _dragIndicator={{ bg: 'neutral.lightest' }}>
          <VStack space={Spacing['stack.xxxs']} w="full" p={Spacing['stack.xxxs']}>
            <HStack alignItems="center" justifyContent="space-between">
              <Text variant="semibold" fontSize={Typography['fontsize.lg']} color="neutral.black">
                Selecione uma das opções
              </Text>
              <IconButton
                icon="Close"
                iconSize="xxs"
                iconColor={Color['neutral.medium']}
                onPress={onClose}
                testId="icon-button-close-action-sheet"
              />
            </HStack>
            {items.map((item) => (
              <Pressable key={item.id} onPress={() => handleSelect(item)}>
                <HStack alignItems="center" justifyContent="space-between">
                  <Text
                    variant="semibold"
                    fontSize={Typography['fontsize.lg']}
                    color="neutral.black">
                    {item.name}
                  </Text>
                  <IconButton
                    icon="ChevronRight"
                    iconSize="xxs"
                    iconColor={Color['neutral.medium']}
                    onPress={onClose}
                    testId="icon-button-select-option"
                  />
                </HStack>
              </Pressable>
            ))}
          </VStack>
        </Actionsheet.Content>
      </Actionsheet>
    </VStack>
  );
};
